"use strict";
/*******************************************************************************

    A class responsible for data requests and responses used in Wallet.

    Copyright:
        Copyright (c) 2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WalletClient = void 0;
var Amount_1 = require("../common/Amount");
var BOAClient_1 = require("../net/BOAClient");
var Types_1 = require("./Types");
var WalletBalance_1 = require("./WalletBalance");
var WalletTransactionFee_1 = require("./WalletTransactionFee");
/**
 * It is a class responsible for data requests and responses used in Wallet.
 */
var WalletClient = /** @class */ (function () {
    /**
     * Constructor
     * @param endpoint    The endpoints of wallet
     */
    function WalletClient(endpoint) {
        if (endpoint === void 0) { endpoint = Types_1.DefaultWalletEndpoint(); }
        this.endpoint = endpoint;
        this.client = new BOAClient_1.BOAClient(this.endpoint.stoa, this.endpoint.agora);
        this.checked_time = new Date(0);
    }
    /**
     * Set the endpoints of wallet, Agora's endpoint & Stoa's endpoint
     * @param endpoint    The endpoints of wallet
     */
    WalletClient.prototype.setEndpoint = function (endpoint) {
        this.endpoint = endpoint;
        this.client = new BOAClient_1.BOAClient(this.endpoint.stoa, this.endpoint.agora);
        this.checked_time = new Date(0);
    };
    WalletClient.prototype.getEndpoint = function () {
        return this.endpoint;
    };
    /**
     * Perform access tests on servers
     */
    WalletClient.prototype.checkServer = function () {
        return __awaiter(this, void 0, void 0, function () {
            var now, duration, e_1, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        now = new Date();
                        duration = now.valueOf() - this.checked_time.valueOf();
                        this.checked_time = now;
                        if (!(duration > WalletClient.CHECK_PERIOD)) return [3 /*break*/, 7];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.client.getAgoraStatus()];
                    case 2:
                        if (!(_a.sent()))
                            return [2 /*return*/, { code: Types_1.WalletResultCode.FailedAccessToAgora, message: Types_1.WalletMessage.FailedAccessToAgora }];
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedAccessToAgora, message: Types_1.WalletMessage.FailedAccessToAgora }];
                    case 4:
                        _a.trys.push([4, 6, , 7]);
                        return [4 /*yield*/, this.client.getStoaStatus()];
                    case 5:
                        if (!(_a.sent()))
                            return [2 /*return*/, { code: Types_1.WalletResultCode.FailedAccessToStoa, message: Types_1.WalletMessage.FailedAccessToStoa }];
                        return [3 /*break*/, 7];
                    case 6:
                        e_2 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedAccessToStoa, message: Types_1.WalletMessage.FailedAccessToStoa }];
                    case 7: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                        }];
                }
            });
        });
    };
    /**
     * Check the balance
     */
    WalletClient.prototype.getBalance = function (address) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, balance, e_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getBalance(address)];
                    case 3:
                        balance = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_3 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestBalance, message: Types_1.WalletMessage.FailedRequestBalance }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: new WalletBalance_1.WalletBalance(balance.address, Amount_1.Amount.make(balance.balance), Amount_1.Amount.make(balance.spendable), Amount_1.Amount.make(balance.frozen), Amount_1.Amount.make(balance.locked), true),
                        }];
                }
            });
        });
    };
    /**
     * Request an appropriate fee based on the size of the transaction to Stoa.
     * @param tx_size The size of the transaction
     */
    WalletClient.prototype.getTransactionFee = function (tx_size) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getTransactionFee(tx_size)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_4 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestTxFee, message: Types_1.WalletMessage.FailedRequestTxFee }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: new WalletTransactionFee_1.WalletTransactionFee(value.tx_size, Amount_1.Amount.make(value.medium), Amount_1.Amount.make(value.high), Amount_1.Amount.make(value.low)),
                        }];
                }
            });
        });
    };
    /**
     * Request a history of transactions.
     * @param address   An address that want to be inquired.
     * @param page_size Maximum record count that can be obtained from one query
     * @param page      The number on the page, this value begins with 1
     * @param type      The parameter `type` is the type of transaction to query. ("payment", "freeze")
     * @param begin     The start date of the range of dates to look up.
     * @param end       The end date of the range of dates to look up.
     * @param peer      This is used when users want to look up only specific
     * Peer is the withdrawal address in the inbound transaction and a deposit address
     * in the outbound transaction address of their counterparts.
     * @deprecated
     */
    WalletClient.prototype.getTransactionsHistory = function (address, page_size, page, type, begin, end, peer) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_5;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getWalletTransactionsHistory(address, page_size, page, type, begin, end, peer)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_5 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_1.WalletResultCode.FailedRequestTransactionHistory,
                                message: Types_1.WalletMessage.FailedRequestTransactionHistory,
                            }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request a history of transactions.
     * @param address   An address that want to be inquired.
     * @param page_size Maximum record count that can be obtained from one query
     * @param page      The number on the page, this value begins with 1
     * @param type      The parameter `type` is the type of transaction to query. ("payment", "freeze")
     * @param begin     The start date of the range of dates to look up.
     * @param end       The end date of the range of dates to look up.
     * @param peer      This is used when users want to look up only specific
     * Peer is the withdrawal address in the inbound transaction and a deposit address
     * in the outbound transaction address of their counterparts.
     */
    WalletClient.prototype.getTransactionHistory = function (address, page_size, page, type, begin, end, peer) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_6;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getWalletTransactionHistory(address, page_size, page, type, begin, end, peer)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_6 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_1.WalletResultCode.FailedRequestTransactionHistory,
                                message: Types_1.WalletMessage.FailedRequestTransactionHistory,
                            }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request a overview of a transaction.
     * @param tx_hash The hash of the transaction
     * @deprecated
     */
    WalletClient.prototype.getTransactionOverview = function (tx_hash) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_7;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getWalletTransactionOverview(tx_hash)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_7 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_1.WalletResultCode.FailedRequestTransactionOverview,
                                message: Types_1.WalletMessage.FailedRequestTransactionOverview,
                            }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request a detail of a transaction.
     * @param tx_hash The hash of the transaction
     */
    WalletClient.prototype.getTransactionDetail = function (tx_hash) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_8;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getWalletTransactionDetail(tx_hash)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_8 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_1.WalletResultCode.FailedRequestTransactionDetail,
                                message: Types_1.WalletMessage.FailedRequestTransactionDetail,
                            }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request pending transactions.
     * @param address The input address of the pending transaction
     */
    WalletClient.prototype.getTransactionsPending = function (address) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_9;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getWalletTransactionsPending(address)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_9 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_1.WalletResultCode.FailedRequestTransactionPending,
                                message: Types_1.WalletMessage.FailedRequestTransactionPending,
                            }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request a pending transaction based on the hash of the transaction.
     * @param tx_hash The hash of the transaction
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    WalletClient.prototype.getPendingTransaction = function (tx_hash) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_10;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getPendingTransaction(tx_hash)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_10 = _a.sent();
                        return [2 /*return*/, {
                                code: Types_1.WalletResultCode.FailedRequestPendingTransaction,
                                message: Types_1.WalletMessage.FailedRequestPendingTransaction,
                            }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request a transaction based on the hash of the transaction.
     * @param tx_hash The hash of the transaction
     */
    WalletClient.prototype.getTransaction = function (tx_hash) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_11;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getTransaction(tx_hash)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_11 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestTransaction, message: Types_1.WalletMessage.FailedRequestTransaction }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Saves the data to the blockchain
     * @param tx The instance of the Transaction
     */
    WalletClient.prototype.sendTransaction = function (tx) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_12;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.sendTransaction(tx)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_12 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedSendTx, message: Types_1.WalletMessage.FailedSendTx }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request an Stoa's current block height.
     */
    WalletClient.prototype.getBlockHeight = function () {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_13;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getBlockHeight()];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_13 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestHeight, message: Types_1.WalletMessage.FailedRequestHeight }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request a fee for a voting transaction
     */
    WalletClient.prototype.getVotingFee = function (payload_size) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_14;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getVotingFee(payload_size)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_14 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestVotingFee, message: Types_1.WalletMessage.FailedRequestVotingFee }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request UTXOs of public key
     * @param address The address of UTXOs
     * @param amount  The amount required
     * @param type    The type of balance (0: spendable, 1: frozen, 2: locked)
     * @param last    The last UTXO hash of previous inquiries.
     * The first time request, this value is undefined.
     * @returns Promise that resolves or
     * rejects with response from the Stoa
     */
    WalletClient.prototype.getUTXOs = function (address, amount, type, last) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_15;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getWalletUTXOs(address, amount, type, last)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_15 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestUTXO, message: Types_1.WalletMessage.FailedRequestUTXO }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Request UTXO's information about the UTXO hash array.
     * @param hashes The hash of UTXOs
     */
    WalletClient.prototype.getUTXOInfo = function (hashes) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_16;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.getUTXOInfo(hashes)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_16 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedRequestUTXOInfo, message: Types_1.WalletMessage.FailedRequestUTXOInfo }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Simple verify payment
     * Check if the transaction is stored in a block.
     * And check if Merkle's proof is valid.
     * @param tx_hash The hash of the transaction
     */
    WalletClient.prototype.verifyPayment = function (tx_hash) {
        return __awaiter(this, void 0, void 0, function () {
            var check_res, value, e_17;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.checkServer()];
                    case 1:
                        check_res = _a.sent();
                        if (check_res.code !== Types_1.WalletResultCode.Success)
                            return [2 /*return*/, check_res];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.client.verifyPayment(tx_hash)];
                    case 3:
                        value = _a.sent();
                        return [3 /*break*/, 5];
                    case 4:
                        e_17 = _a.sent();
                        return [2 /*return*/, { code: Types_1.WalletResultCode.FailedVerifyPayment, message: Types_1.WalletMessage.FailedVerifyPayment }];
                    case 5: return [2 /*return*/, {
                            code: Types_1.WalletResultCode.Success,
                            message: Types_1.WalletMessage.Success,
                            data: value,
                        }];
                }
            });
        });
    };
    /**
     * Minimum amount of time the server status should be checked
     * @private
     */
    WalletClient.CHECK_PERIOD = 5000;
    return WalletClient;
}());
exports.WalletClient = WalletClient;
//# sourceMappingURL=WalletClient.js.map